import DetailsCard from "@/components/DetailsCard";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { Event, EventRegistrationType } from "../../../types";
import { toDateFormat } from "@/lib/dateHelpers";
import { getEventTypeName } from "../../../utils";
import type { University } from "@/pages/education/universities/types";
import { Link } from "react-router-dom";
import type { Path } from "@/router";

interface EventDetailsCardsProps {
  event: Event;
}

function formatEventAddress(event: Event) {
  if (!event.address1) return { line1: "", line2: "" };

  const line1 = event.address1;
  const stateAndZip = [event.state, event.zip].filter(Boolean).join(" ");
  const line2 = stateAndZip ? `${event.city}, ${stateAndZip}` : event.city;

  return { line1, line2 };
}

export function EventDetailsCards({ event }: EventDetailsCardsProps) {
  const isEmptyValue = (value: any) => {
    return value === null || value === undefined || value === "";
  };

  const isRecruitment = getEventTypeName(event) === "Recruitment";
  const createdBy = event.createdBy?.name || "—";

  const registrationOptionsSection = {
    heading: "Registration Options",
    options: [
      {
        overline: "Registration Opens",
        headline: toDateFormat(event.registrationOpenAt?.toString(), { tz: event.timezone }),
        iconName: "today" as const
      },
      {
        overline: "Registration Closes",
        headline: toDateFormat(event.registrationCloseAt?.toString(), { tz: event.timezone }),
        iconName: "today" as const
      },
      ...(event.sessionCount > 1
        ? [
            {
              overline: "Multiple Sessions",
              headline: `${event.sessionCount} Sessions`,
              supportingText:
                event.registrationType === EventRegistrationType.INDIVIDUAL
                  ? "Registrant can select individual sessions"
                  : "Registration includes ALL sessions",
              iconName: "event" as const
            }
          ]
        : []),
      {
        overline: "Waitlist",
        headline: event.hasWaitlist ? "Enabled" : "Disabled",
        iconName: "today" as const
      }
    ].filter((option) => !isEmptyValue(option.headline))
  };

  const locationSection = {
    heading: "Location",
    options: [
      {
        overline: "Managing Section",
        headline: event.section?.id ? (
          <Link to={`/sections/${event.section.id}` as Path} className="text-sys-brand-primary hover:underline">
            {event.section.name}
          </Link>
        ) : (
          event.section?.name
        ),
        iconName: "domain" as const
      },
      ...(event.universities && event.universities.length > 0
        ? [
            {
              overline: "Managing Universities",
              headline: (
                <div>
                  {event.universities?.map((university: University) => (
                    <div key={university.id}>{university.name}</div>
                  ))}
                </div>
              ),
              iconName: "school" as const
            }
          ]
        : []
      ).filter((option) => !isEmptyValue(option.headline)),
      ...(event.locationType?.toLowerCase() === "virtual"
        ? [
            {
              overline: "Event Location",
              headline: "Virtual",
              supportingText: event.location,
              iconName: "location_on" as const
            }
          ]
        : [
            {
              overline: "Facility",
              headline: event.facility?.id ? (
                <Link
                  to={`/facilities/${event.facility.id}/details` as Path}
                  className="text-sys-brand-primary hover:underline"
                >
                  {event.facility.name}
                </Link>
              ) : (
                event.facility?.name
              ),
              supportingText: (() => {
                const address = formatEventAddress(event);
                const addressText =
                  address.line1 && address.line2
                    ? `${address.line1}, ${address.line2}`
                    : address.line1 || address.line2;
                return [addressText, event.location].filter(Boolean).join(" · ") || undefined;
              })(),
              iconName: "location_on" as const
            }
          ])
    ].filter((option) => !isEmptyValue(option.headline))
  };

  // Custom fields mapping
  const customFieldsMap = {
    date_of_birth: "Date of Birth",
    gender: "Gender",
    disclaimer: "Terms and Conditions",
    ada_accommodations: "ADA Accommodations",
    additional_guests: "Additional Guests"
  };

  const additionalInfoAndEventDetailsSection = {
    sections: [
      ...(event.customFields && event.customFields.length > 0
        ? [
            {
              heading: "Additional Information Required",
              options: event.customFields
                .filter((field) => customFieldsMap[field as keyof typeof customFieldsMap])
                .map((field) => ({
                  headline: customFieldsMap[field as keyof typeof customFieldsMap],
                  iconName: "assignment" as const
                }))
            }
          ]
        : []),
      {
        heading: "Event Details",
        options: [
          {
            overline: isRecruitment ? "Leads" : "Registrations",
            headline: event.registrationCount?.toString() || "0",
            iconName: "group" as const
          },
          {
            overline: "Event Type",
            headline: getEventTypeName(event) || "",
            iconName: "today" as const
          },
          ...(isRecruitment
            ? [
                {
                  overline: "Created By",
                  headline: createdBy,
                  iconName: "today" as const
                }
              ]
            : []),
          {
            overline: "Last Modified",
            headline: toDateFormat(event.updatedAt?.toString(), { tz: event.timezone }),
            iconName: "today" as const
          }
        ].filter((option) => !isEmptyValue(option.headline))
      }
    ].filter((section) => section.options.length > 0)
  };

  return (
    <div className="flex flex-col gap-4">
      {!isRecruitment && registrationOptionsSection.options.length > 0 && (
        <DetailsCard heading={registrationOptionsSection.heading} options={registrationOptionsSection.options} />
      )}

      {locationSection.options.length > 0 && (
        <DetailsCard heading={locationSection.heading} options={locationSection.options} />
      )}

      {additionalInfoAndEventDetailsSection.sections.length > 0 && (
        <MultiSectionDetailsCard sections={additionalInfoAndEventDetailsSection.sections} />
      )}
    </div>
  );
}
